import { useState, useCallback } from 'react';
import { CvTemplateService } from '@/services/cvTemplateService';
import { generateAndSaveCv } from '@/services/cvGenerationService';
import { Candidate } from '@/types/candidate';
import { CandidateExperience, CandidateSkill } from '@/hooks/candidate/useCandidateExperiences';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface GenerateCvOptions {
	templateId: string;
	candidate: Candidate;
	experiences: CandidateExperience[];
	skills: CandidateSkill[];
	organizationName?: string;
	polishWithAi?: boolean;
	language?: string;
}

export const useCvGeneration = () => {
	const [generating, setGenerating] = useState(false);
	const [progress, setProgress] = useState<string | null>(null);
	const { toast } = useToast();
	const { t } = useTranslation();

	const generateCv = useCallback(async (options: GenerateCvOptions) => {
		setGenerating(true);
		setProgress(t('cvGeneration.replacingVariables', 'Replacing template variables...'));

		try {
			const template = await CvTemplateService.getById(options.templateId);
			if (!template)
				throw new Error('Template not found');

			const result = await generateAndSaveCv({
				template,
				candidate: options.candidate,
				experiences: options.experiences,
				skills: options.skills,
				organizationName: options.organizationName,
				polishWithAi: options.polishWithAi,
				language: options.language,
				onProgress: (step) => {
					const messages: Record<string, string> = {
						polishingWithAi: t('cvGeneration.polishingWithAi', 'Polishing content with AI...'),
						replacingVariables: t('cvGeneration.replacingVariables', 'Replacing template variables...'),
						generatingPdf: t('cvGeneration.generatingPdf', 'Generating PDF...'),
						savingDocument: t('cvGeneration.savingDocument', 'Saving document...'),
					};
					setProgress(messages[step] || step);
				},
			});

			toast({
				title: t('cvGeneration.success', 'CV generated successfully'),
				description: `CV saved to candidate documents.`,
			});

			return result;
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : t('cvGeneration.error', 'Failed to generate CV');
			toast({
				title: "Error",
				description: errorMessage,
				variant: "destructive",
			});
			throw err;
		} finally {
			setGenerating(false);
			setProgress(null);
		}
	}, [toast, t]);

	return {
		generating,
		progress,
		generateCv,
	};
};
